import React from 'react';
import {
  Box,
  Chip,
  Paper,
  Stack,
  Typography,
} from '@mui/material';
import {
  CheckCircle2,
  File,
  FileSpreadsheet,
  FileText,
  FolderOpen,
} from 'lucide-react';
import { ApplicantDocument } from '../../../../../utils/types/JobOpenings';

export interface DocumentsTabProps {
  documents: ApplicantDocument[];
  selectedDocumentId?: string;
  onDocumentSelect: (doc: ApplicantDocument) => void;
  // Fallback for resume if documents is empty
  pdfUrl?: string;
  baseFilename?: string;
  uploadedAt?: number | string;
}

export const DocumentsTab = ({
  documents,
  selectedDocumentId,
  onDocumentSelect,
  pdfUrl,
  baseFilename,
  uploadedAt,
}: DocumentsTabProps) => {
  // Synthesize a resume document if documents is empty and pdfUrl exists
  const effectiveDocuments: ApplicantDocument[] =
    (!documents || documents.length === 0) && pdfUrl
      ? [
          {
            id: 'resume-fallback',
            jobApplicantId: '',
            jobOpeningId: '',
            requirementKey: 'resume',
            requirementLabel: 'Resume',
            s3Filename: baseFilename || 'Resume.pdf',
            baseFilename: baseFilename || 'Resume.pdf',
            fileHash: '',
            uploadedAt: typeof uploadedAt === 'number' ? uploadedAt : 0,
          } as ApplicantDocument,
        ]
      : documents || [];

  // Group documents by requirementLabel
  const groups = effectiveDocuments.reduce(
    (acc: Record<string, ApplicantDocument[]>, doc) => {
      const groupName = doc.requirementLabel || 'Other';
      if (!acc[groupName]) {
        acc[groupName] = [];
      }
      acc[groupName].push(doc);
      return acc;
    },
    {}
  );

  const sortedGroupNames = Object.keys(groups).sort((a, b) => {
    if (a.toLowerCase() === 'resume') return -1;
    if (b.toLowerCase() === 'resume') return 1;
    return a.localeCompare(b);
  });

  const getDocumentIcon = (filename?: string) => {
    const lower = (filename || '').toLowerCase();
    if (lower.endsWith('.pdf')) {
      return (
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '10px',
            bgcolor: 'rgba(239, 68, 68, 0.1)',
            color: '#dc2626',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FileText size={20} />
        </Box>
      );
    }
    if (lower.endsWith('.xlsx') || lower.endsWith('.xls') || lower.endsWith('.csv')) {
      return (
        <Box
          sx={{
            width: 42,
            height: 42,
            borderRadius: '10px',
            bgcolor: 'rgba(16, 185, 129, 0.1)',
            color: '#059669',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            flexShrink: 0,
          }}
        >
          <FileSpreadsheet size={20} />
        </Box>
      );
    }
    return (
      <Box
        sx={{
          width: 42,
          height: 42,
          borderRadius: '10px',
          bgcolor: 'rgba(37, 99, 235, 0.1)',
          color: '#2563eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
        }}
      >
        <File size={20} />
      </Box>
    );
  };

  if (effectiveDocuments.length === 0) {
    return (
      <Paper
        elevation={0}
        sx={{
          py: 8,
          px: 3,
          textAlign: 'center',
          borderRadius: '16px',
          border: '2px dashed',
          borderColor: 'divider',
          bgcolor: 'grey.50',
          my: 2,
        }}
      >
        <Box
          sx={{
            width: 56,
            height: 56,
            borderRadius: '50%',
            bgcolor: 'grey.100',
            color: 'text.secondary',
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            mx: 'auto',
            mb: 2,
          }}
        >
          <FolderOpen size={28} />
        </Box>
        <Typography
          variant="subtitle1"
          sx={{ fontWeight: 600, color: 'text.primary', mb: 0.5 }}
        >
          No Documents Found
        </Typography>
        <Typography
          variant="body2"
          sx={{ color: 'text.secondary', maxWidth: 300, mx: 'auto' }}
        >
          No documents have been uploaded for this candidate yet.
        </Typography>
      </Paper>
    );
  }

  return (
    <Box sx={{ py: 1.5, display: 'flex', flexDirection: 'column', gap: 3 }}>
      {sortedGroupNames.map((groupName, groupIdx) => (
        <Box key={groupIdx}>
          {/* Section Category Title */}
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{ mb: 1.5, pb: 0.5 }}
          >
            <Typography
              variant="subtitle2"
              sx={{ fontWeight: 700, fontSize: '14px', color: 'text.primary' }}
            >
              {groupName}
            </Typography>
            <Chip
              label={`${groups[groupName].length} File${groups[groupName].length > 1 ? 's' : ''}`}
              size="small"
              sx={{
                height: '20px',
                fontSize: '11px',
                fontWeight: 600,
                bgcolor: 'grey.100',
                color: 'text.secondary',
              }}
            />
          </Stack>

          {/* Document Cards */}
          <Stack spacing={1.5}>
            {groups[groupName].map((doc) => {
              const isSelected =
                selectedDocumentId === doc.id ||
                (!selectedDocumentId && doc.id === 'resume-fallback');

              return (
                <Paper
                  key={doc.id}
                  elevation={isSelected ? 2 : 0}
                  onClick={() => onDocumentSelect(doc)}
                  sx={{
                    p: 2,
                    borderRadius: '12px',
                    border: '1.5px solid',
                    borderColor: isSelected ? 'success.main' : 'divider',
                    bgcolor: isSelected
                      ? 'rgba(16, 185, 129, 0.04)'
                      : 'background.paper',
                    cursor: 'pointer',
                    transition: 'all 0.2s ease',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: 2,
                    '&:hover': {
                      borderColor: isSelected ? 'success.main' : 'primary.main',
                      transform: 'translateY(-1px)',
                      boxShadow: '0 4px 12px rgba(0, 0, 0, 0.05)',
                    },
                  }}
                >
                  <Stack direction="row" spacing={2} alignItems="center" sx={{ minWidth: 0, flex: 1 }}>
                    {getDocumentIcon(doc.baseFilename)}
                    <Box sx={{ minWidth: 0, flex: 1 }}>
                      <Typography
                        variant="subtitle2"
                        noWrap
                        sx={{
                          fontWeight: 600,
                          fontSize: '13.5px',
                          color: isSelected ? 'success.dark' : 'text.primary',
                        }}
                      >
                        {doc.baseFilename}
                      </Typography>
                      <Typography
                        variant="caption"
                        sx={{ color: 'text.secondary', fontSize: '11.5px', display: 'block', mt: 0.25 }}
                      >
                        Uploaded{' '}
                        {doc.uploadedAt
                          ? new Date(
                              typeof doc.uploadedAt === 'number'
                                ? doc.uploadedAt * 1000
                                : doc.uploadedAt
                            ).toLocaleDateString('en-US', {
                              month: 'short',
                              day: 'numeric',
                              year: 'numeric',
                            })
                          : 'Recently'}
                      </Typography>
                    </Box>
                  </Stack>

                  {/* Selected Indicator */}
                  {isSelected && (
                    <Box
                      sx={{
                        color: 'success.main',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        flexShrink: 0,
                      }}
                    >
                      <CheckCircle2 size={18} />
                    </Box>
                  )}
                </Paper>
              );
            })}
          </Stack>
        </Box>
      ))}
    </Box>
  );
};
